import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Account, AccountManagementColumn, SortState } from '../types';
import { RowClickedEvent, type ColDef, SortChangedEvent } from "ag-grid-community";
import { AgGridAngular } from 'ag-grid-angular';
import { AgTooltipCellComponent } from '../../../shared/components/ag-tooltip-cell/ag-tooltip-cell.component';
import { getManagementDateFormat, getManagementUtcDateFormat } from '../../../utils/time';
import { MANAGEMENT_GRID_MODULES, MANAGEMENT_GRID_THEME, createManagementGridOptions, toSortState } from '../management-grid';
import { subscriptionLabel } from '../subscription.utils';

@Component({
  selector: 'app-accounts-table',
  imports: [AgGridAngular],
  templateUrl: './accounts-table.component.html',
  styleUrl: './accounts-table.component.scss'
})
export class AccountsTableComponent {
  @Input() rows: Account[] = [];
  @Output() rowClicked = new EventEmitter<Account>();
  @Output() sortChanged = new EventEmitter<SortState>();

  readonly theme = MANAGEMENT_GRID_THEME;
  readonly modules = MANAGEMENT_GRID_MODULES;
  readonly gridOptions = createManagementGridOptions();
  colDefs: ColDef[] = this.getColDef();

  public onRowClicked = (event: RowClickedEvent): void => {
    this.rowClicked.emit(event.data as Account);
  };

  public onSortChanged(event: SortChangedEvent): void {
    const sort = toSortState(event);
    if (sort) {
      this.sortChanged.emit(sort);
    }
  }

  private getColDef(): ColDef[] {
    return [
      {
        field: AccountManagementColumn.NAME,
        flex: 3, // names are long — give this column the bulk of the width
      },
      {
        field: 'Users',
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data?.[AccountManagementColumn.USERS].length;
        },
        sortable: false,
        maxWidth: 110, // value is just a count
      },
      {
        field: AccountManagementColumn.BRANDS,
        cellRenderer: AgTooltipCellComponent,
        valueGetter: (params: any) => {
          return params.data[AccountManagementColumn.BRANDS].length
        },
        sortable: false,
        maxWidth: 110, // value is just a count
      },
      {
        headerName: 'Subscription',
        field: AccountManagementColumn.SUBSCRIPTION,
        valueGetter: (params: any) => {
          return subscriptionLabel(params.data?.[AccountManagementColumn.SUBSCRIPTION]);
        },
        sortable: false, // server-side sort maps fields to DB columns; this one is derived
        maxWidth: 130,
      },
      {
        headerName: 'Trial ends',
        field: AccountManagementColumn.TRIAL_ENDS_AT,
        valueGetter: (params: any) => {
          const trialEndsAt = params.data?.[AccountManagementColumn.TRIAL_ENDS_AT];
          // UTC variant: a trial ending at UTC midnight must show the same calendar date the
          // drawer's date field shows, for viewers in any timezone.
          return trialEndsAt ? getManagementUtcDateFormat(trialEndsAt) : '—';
        },
        sortable: false,
        maxWidth: 140,
      },
      {
        field: AccountManagementColumn.CREATED_AT,
        valueGetter: (params: any) => {
          return getManagementDateFormat(params.data[AccountManagementColumn.CREATED_AT])
        }
      },
    ];
  }
}
